import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FormCaption, InformSpan, SixDigitInput } from '../../common/components';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { APP_KEYS } from '../../common/consts';
import { authService } from '../service/auth.service';
import { useThrottle } from '../../hooks/useThrottle';

interface Inputs {
  code: string;
}

export const PasswordRecovery = () => {
  const { t } = useTranslation();
  const [serverError, setServerError] = React.useState('');
  const [formattedValue, setFormattedValue] = React.useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string })?.email;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<Inputs>();

  const { mutate: sendRecoveryCode } = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.RECOVERY_CODE],
    mutationFn: (data: Inputs) =>
      authService.checkRecoveryCode({ token: data.code }),
    onSuccess: (token) => {
      navigate(`${ROUTER_LINKS.CREATE_NEW_PASSWORD}/${token}`, {
        state: { email }
      });
    },
    onError: (err: any) => {
      if (err.response?.data?.message === 'Invalid token') {
        setServerError(t('auth.codeIncorrect'));
      }
    }
  });
  const throttledSendRecoveryCode = useThrottle(sendRecoveryCode);

  const { mutate: resendCode } = useMutation({
    mutationFn: (emailAddr: string) =>
      authService.resendRecoveryCode({ email: emailAddr }),
    onSuccess: () => {
      toast.success(t('auth.recoveryCodeSent'));
    }
  });
  const throttledResendCode = useThrottle(resendCode);

  const onSubmit = (data: Inputs) => {
    const code = data.code.replace(/\s/g, '');
    if (code.length === 6) {
      throttledSendRecoveryCode({ code });
    }
  };

  React.useEffect(() => {
    const code = formattedValue.replace(/\s/g, '');
    setValue('code', formattedValue);
    if (code.length === 6) {
      throttledSendRecoveryCode({ code });
    }
  }, [formattedValue]);

  return (
    <div className={`relative ${serverError ? 'pt-[22px] md:pt-[32px]' : ''}`}>
      {serverError && (
        <div className="absolute flex justify-center gap-[8px] items-center top-[-22px] md:top-[-32px] left-[-22px] md:left-[-32px] w-[345px] md:w-[456px] h-[32px] bg-[#E42C2C] text-white text-center rounded-t-[42px] py-[6px] font-body text-[14px] font-regular">
          {serverError}
        </div>
      )}
      <FormCaption>{t('auth.recoveryTitle')}</FormCaption>
      <InformSpan>{t('auth.recoverySubtitle', { email: email ?? '' })}</InformSpan>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <SixDigitInput
          errors={errors}
          name="code"
          setFormattedValue={setFormattedValue}
          formattedValue={formattedValue}
          register={register}
        >
          <div className="flex gap-[10px] justify-center text-center my-[24px]">
            <p className="text-[16px] font-regular font-body text-gray-900">
              {t('auth.dontSeeCode')}
            </p>
            <div
              className="cursor-pointer"
              onClick={() => email && throttledResendCode(email)}
            >
              <span className="text-[16px] font-medium font-body text-base-black hover:opacity-60">
                {t('auth.resendCode')}
              </span>
            </div>
          </div>
        </SixDigitInput>
      </form>
    </div>
  );
};
